import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/AdditionalServicePrices_SalvarInput';
import '../contracts/AdditionalServicePrices_RemoverInput';

import '../contracts/AdditionalServicePrices_ListaResponse';
import '../contracts/AdditionalServicePrices_ListaPorFranquiaResponse';
import '../contracts/AdditionalServicePrices_GetResponse';
import '../contracts/AdditionalServicePrices_SalvarResponse';
import '../contracts/AdditionalServicePrices_RemoverResponse';
import type { AdditionalServicePrices_SalvarInput } from '../contracts/AdditionalServicePrices_SalvarInput';
import type { AdditionalServicePrices_RemoverInput } from '../contracts/AdditionalServicePrices_RemoverInput';

/**
 * Composable da GESTÃO do preço de adicional POR FRANQUIA
 * (so_additional_service_prices) — a irmã da useSOServicePriorities, pelo
 * mesmo desenho: o adicional é GLOBAL e cada franquia tem a SUA linha de
 * preço/prazo, com fallback no default global quando não há linha. A camada
 * nasceu na migração Zoho E3 (opção 3 do usuário).
 *
 * "Remover" é soft-delete com SEMÂNTICA: active=0 = a franquia DESLIGOU o
 * adicional (ele some do modal de item dela e continua na gestão para religar).
 */
export function useSOAdditionalServicePrices() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const basePath = '/index.php/so/additional_service_prices';

  const fetchAll = async () => {
    log('useSOAdditionalServicePrices', 'fetchAll', 'Fetching all price rows');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'AdditionalServicePrices_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar preços de adicionais');
    } catch (err) {
      logError('useSOAdditionalServicePrices', 'fetchAll', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchByFranchise = async (franchiseId: number) => {
    log('useSOAdditionalServicePrices', 'fetchByFranchise', 'Fetching by franchise', { franchiseId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista_por_franquia/${franchiseId}`, { responseContract: 'AdditionalServicePrices_ListaPorFranquiaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar preços da franquia');
    } catch (err) {
      logError('useSOAdditionalServicePrices', 'fetchByFranchise', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getById = async (id: number) => {
    log('useSOAdditionalServicePrices', 'getById', 'Fetching price row', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get/${id}`, { responseContract: 'AdditionalServicePrices_GetResponse' });
      if (response.success) {
        return response.dados?.row ?? null;
      }
      throw new Error(response.error || 'Erro ao buscar preço do adicional');
    } catch (err) {
      logError('useSOAdditionalServicePrices', 'getById', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: AdditionalServicePrices_SalvarInput) => {
    log('useSOAdditionalServicePrices', 'save', 'Saving price row', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('AdditionalServicePrices_SalvarInput', data);
      const response = await httpService.post(`${basePath}/salvar`, data, { responseContract: 'AdditionalServicePrices_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar preço do adicional');
    } catch (err) {
      logError('useSOAdditionalServicePrices', 'save', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (data: AdditionalServicePrices_RemoverInput) => {
    log('useSOAdditionalServicePrices', 'remove', 'Disabling price row', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('AdditionalServicePrices_RemoverInput', data);
      const response = await httpService.post(`${basePath}/remover`, data, { responseContract: 'AdditionalServicePrices_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao desligar o adicional na franquia');
    } catch (err) {
      logError('useSOAdditionalServicePrices', 'remove', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAll,
    fetchByFranchise,
    getById,
    save,
    remove
  };
}
